import { Button, Title } from '@yoast/ui-library';
import { useRef, useState } from 'react';

import BrandProfileFieldCard from './BrandProfileFieldCard';
import ConfirmBrandProfileModal from './ConfirmBrandProfileModal';
import CountrySelect from './CountrySelect';
import LanguageSelect from './LanguageSelect';
import ThresholdProgressBar from './ThresholdProgressBar';
import type { Extracted, Geography } from './fieldUtils';
import {
  CITY_TOOLTIP,
  countFilled,
  countryLabel,
  DESCRIPTION_MAX_LENGTH,
  FIELD_MAX_LENGTH,
  GEOGRAPHY_MAX_LENGTH,
  EMPTY_GEOGRAPHY,
  FIELD_LABELS,
  FIELD_TOOLTIPS,
  formatFieldDisplay,
  formatFieldForEdit,
  isComplete,
  isFieldFilled,
  isMultilineField,
  isRequiredField,
  meetsEnough,
  meetsOptimal,
  ORDERED_FIELDS,
  parseFieldFromEdit,
  REGION_TOOLTIP,
  TOTAL_FIELDS,
  validateWebsiteUrl,
} from './fieldUtils';

export type { Extracted, Geography } from './fieldUtils';

type Props = {
  extracted: Extracted | null;
  isStreaming: boolean;
  isGenerating: boolean;
  onSubmitField: (patch: Partial<Extracted>) => void;
  onGenerate: () => void;
};

const CollectedInfoPanel = ({
  extracted,
  isStreaming,
  isGenerating,
  onSubmitField,
  onGenerate,
}: Props) => {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const filledCount = countFilled(extracted);
  const hasEnough = meetsEnough(extracted);
  const hasOptimal = meetsOptimal(extracted);
  const complete = isComplete(extracted);

  // Once more than half the fields are filled, show every field — including
  // still-empty ones — so the user can see and complete the whole profile.
  // Below that threshold, show only what's been collected so far.
  const showAllFields = filledCount > TOTAL_FIELDS / 2;

  // Build the cards to show. Geography is stored as one `target_geography`
  // object but is surfaced as separate Country / Region / City cards. Country is
  // an ISO alpha-2 code picked from a dropdown; Region and City only appear once
  // a country is set.
  const geo = (extracted?.target_geography ?? EMPTY_GEOGRAPHY) as Geography;
  const countryValue = (geo.country ?? '').trim();
  const hasCountry = countryValue.length > 0;
  const showRegionCity = hasCountry;

  const setGeo = (patch: Partial<Geography>) =>
    onSubmitField({ target_geography: { ...geo, ...patch } } as Partial<Extracted>);

  type Card = {
    key: string;
    label: string;
    displayValue: string;
    editInitialValue: string;
    onSubmit: (raw: string) => void;
    maxLength?: number;
    renderEditField?: (
      draft: string,
      setDraft: (value: string) => void,
      onKeyDown: (event: React.KeyboardEvent) => void,
    ) => React.ReactNode;
    required?: boolean;
    inputType?: 'text' | 'url';
    multiline?: boolean;
    validate?: (value: string) => string | null;
    tooltip?: string;
  };

  const cards: Card[] = [];
  for (const field of ORDERED_FIELDS) {
    if (field === 'target_geography') {
      if (hasCountry || showAllFields) {
        cards.push({
          key: 'geo_country',
          label: 'Country',
          tooltip: FIELD_TOOLTIPS.target_geography,
          // Stored as an ISO code; shown to the user as the country name.
          displayValue: countryLabel(geo.country),
          editInitialValue: geo.country ?? '',
          onSubmit: (raw) => {
            const country = raw.trim() || null;
            // Region/City are meaningless without a country, so clear them when
            // the country is removed.
            setGeo(country ? { country } : { country: null, region: null, city: null });
          },
          // Forward Escape-to-cancel only. Enter is consumed by the Autocomplete
          // to select the highlighted option; forwarding it would race the card's
          // save against setDraft and persist the wrong value.
          renderEditField: (draft, setDraft, onKeyDown) => (
            <div
              onKeyDown={(event) => {
                if (event.key === 'Escape') onKeyDown(event);
              }}
            >
              <CountrySelect
                value={draft || null}
                onChange={(code) => setDraft(code ?? '')}
              />
            </div>
          ),
        });
      }
      if (showRegionCity) {
        cards.push({
          key: 'geo_region',
          label: 'Region',
          tooltip: REGION_TOOLTIP,
          displayValue: geo.region ?? '',
          editInitialValue: geo.region ?? '',
          onSubmit: (raw) => setGeo({ region: raw.trim() || null }),
          maxLength: GEOGRAPHY_MAX_LENGTH,
        });
        cards.push({
          key: 'geo_city',
          label: 'City',
          tooltip: CITY_TOOLTIP,
          displayValue: geo.city ?? '',
          editInitialValue: geo.city ?? '',
          onSubmit: (raw) => setGeo({ city: raw.trim() || null }),
          maxLength: GEOGRAPHY_MAX_LENGTH,
        });
      }
      continue;
    }

    if (!showAllFields && !isFieldFilled(field, extracted?.[field])) {
      continue;
    }

    const isLanguage = field === 'target_language';
    cards.push({
      key: field,
      label: FIELD_LABELS[field],
      tooltip: FIELD_TOOLTIPS[field],
      displayValue: formatFieldDisplay(field, extracted?.[field]),
      editInitialValue: formatFieldForEdit(field, extracted?.[field]),
      onSubmit: (raw) =>
        onSubmitField({ [field]: parseFieldFromEdit(field, raw) } as Partial<Extracted>),
      // Language is picked from a fixed list, so it has no free-text length cap.
      maxLength:
        field === 'description'
          ? DESCRIPTION_MAX_LENGTH
          : isLanguage
            ? undefined
            : FIELD_MAX_LENGTH,
      required: isRequiredField(field),
      inputType: field === 'website_url' ? 'url' : undefined,
      // Long-text and comma-separated list fields (description, key offerings,
      // competitors, …) only show the first few words in a single-line input, so
      // edit them in a multi-line textarea instead.
      multiline: isMultilineField(field),
      validate: field === 'website_url' ? validateWebsiteUrl : undefined,
      // Single-select language picker (like the country card). Forward
      // Escape-to-cancel only; Enter is consumed by the Autocomplete to select
      // the highlighted option.
      renderEditField: isLanguage
        ? (draft, setDraft, onKeyDown) => (
            <div
              onKeyDown={(event) => {
                if (event.key === 'Escape') onKeyDown(event);
              }}
            >
              <LanguageSelect
                value={draft || null}
                onChange={(name) => setDraft(name ?? '')}
              />
            </div>
          )
        : undefined,
    });
  }

  // Group empty cards at the bottom so they don't sit interspersed between
  // filled ones once the "show all fields" threshold is crossed. Required
  // fields (brand_name, description) stay pinned at the top even when empty so
  // the user is still nudged to fill them. `filter` is stable, so each group
  // keeps its ORDERED_FIELDS sequence.
  const isCardFilled = (card: Card) => card.displayValue.trim().length > 0;
  const orderedCards: Card[] = [
    ...cards.filter((card) => card.required),
    ...cards.filter((card) => !card.required && isCardFilled(card)),
    ...cards.filter((card) => !card.required && !isCardFilled(card)),
  ];

  // Lock each card's stagger index on first appearance. A plain "seen" set
  // would reset the inline animation-delay back to 0 on the next re-render
  // (which happens as `isStreaming` toggles), restarting the whole batch in
  // lockstep. The counter is monotonic across batches so a card that appears
  // later doesn't collide with the first batch's delays.
  const staggerByFieldRef = useRef<Map<string, number>>(new Map());
  const nextStaggerIndexRef = useRef(0);
  for (const card of orderedCards) {
    if (!staggerByFieldRef.current.has(card.key)) {
      staggerByFieldRef.current.set(card.key, nextStaggerIndexRef.current);
      nextStaggerIndexRef.current += 1;
    }
  }

  const handleConfirmGenerate = () => {
    // Close the modal and let the page-level button + inline error reflect the
    // brand-creation state (the page navigates away on success).
    setConfirmOpen(false);
    onGenerate();
  };

  return (
    <aside
      aria-label="Brand profile"
      className="yst-flex yst-min-h-0 yst-flex-col yst-rounded-xl yst-bg-white yst-shadow-sm"
    >
      <div className="yst-shrink-0 yst-space-y-6 yst-p-6">
        <div>
          <Title size="3" as="h2" className="yst-text-lg yst-font-medium yst-text-slate-900">
            Brand profile
          </Title>
          <p className="yst-mt-1 yst-text-sm yst-text-slate-500">
            Add more details for sharper queries.
          </p>
        </div>

        <ThresholdProgressBar
          hasEnough={hasEnough}
          hasOptimal={hasOptimal}
          isComplete={complete}
          filledCount={filledCount}
          totalCount={TOTAL_FIELDS}
        />
      </div>

      <div className="yst-min-h-0 yst-flex-1 yst-overflow-y-auto yst-px-6 yst-pb-6">
        {cards.length === 0 ? (
          <p className="yst-text-sm yst-italic yst-text-slate-400">
            Start the chat and your brand details will appear here as we collect
            them.
          </p>
        ) : (
          <div className="yst-space-y-2">
            {orderedCards.map((card) => (
              <BrandProfileFieldCard
                key={card.key}
                label={card.label}
                displayValue={card.displayValue}
                editInitialValue={card.editInitialValue}
                disabled={isStreaming}
                staggerIndex={staggerByFieldRef.current.get(card.key) ?? 0}
                maxLength={card.maxLength}
                renderEditField={card.renderEditField}
                required={card.required}
                inputType={card.inputType}
                multiline={card.multiline}
                validate={card.validate}
                tooltip={card.tooltip}
                onSubmit={card.onSubmit}
              />
            ))}
          </div>
        )}
      </div>

      <div className="yst-shrink-0 yst-rounded-b-xl yst-border-t yst-border-slate-200 yst-bg-slate-50 yst-px-6 yst-py-4">
        <Button
          className="yst-h-10 yst-w-full"
          disabled={!hasEnough || isStreaming || isGenerating}
          isLoading={isGenerating}
          onClick={() => setConfirmOpen(true)}
        >
          {isGenerating ? 'Generating queries…' : 'Review & generate queries'}
        </Button>
      </div>

      <ConfirmBrandProfileModal
        isOpen={confirmOpen}
        extracted={extracted}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmGenerate}
      />
    </aside>
  );
};

export default CollectedInfoPanel;
